import queryString from 'query-string';

import { handleRefreshToken } from 'utils/refreshToken';
import { API_URL, HEADER_ACCEPT_HR } from 'utils/apiConstants';
import { UseSearchProgramsQueryParams } from 'interfaces/hooks/programsHooksInterfaces';
import { DataPeriod } from 'interfaces/global/globalInterfaces';
import { FetchError } from 'utils/FetchError';
import { getNonEndedServicePeriodUuids } from 'utils/getNonEndedServicePeriodUuids';

/**
 * Единый источник ключа и запроса для списка программ.
 *
 * Ключ и запрос вынесены сюда, чтобы предзагрузка (prefetch) и хук useSearchPrograms
 * гарантированно попадали в одну ячейку кэша react-query: любое расхождение в ключе
 * молча превращает предзагрузку в лишний запрос.
 */
export interface ProgramsQueryParams {
  companyUuid: string;
  periodId: string;
  servicePeriodIn: string;
  programId: string;
  valuesServices: string;
  cityId: string;
  isAttachProgramsQuery: boolean;
}

export interface BuildProgramsQueryParamsInput {
  companyUuid: string;
  periodId: string;
  periods?: DataPeriod[] | null;
  excludeEndedPeriodPrograms?: boolean;
  programId?: string;
  valuesServices?: string;
  cityId?: string;
}

export const buildProgramsQueryParams = ({
  companyUuid,
  periodId,
  periods,
  excludeEndedPeriodPrograms = false,
  programId = '',
  valuesServices = '',
  cityId = '',
}: BuildProgramsQueryParamsInput): ProgramsQueryParams => {
  const servicePeriodIn = excludeEndedPeriodPrograms ? getNonEndedServicePeriodUuids(periods) : '';

  return {
    companyUuid,
    periodId,
    servicePeriodIn,
    programId,
    valuesServices,
    cityId,
    isAttachProgramsQuery: excludeEndedPeriodPrograms && !!servicePeriodIn,
  };
};

export const getProgramsQueryKey = (params: ProgramsQueryParams): string[] => [
  'dataPrograms',
  params.isAttachProgramsQuery ? 'attach' : params.companyUuid,
  params.isAttachProgramsQuery ? params.servicePeriodIn : params.periodId,
  params.programId,
  params.valuesServices,
  params.cityId,
];

export const fetchProgramsPage = async (params: ProgramsQueryParams, pageParam = 1): Promise<unknown> => {
  await handleRefreshToken();

  const queryParams: UseSearchProgramsQueryParams = {
    page: pageParam,
    per_page: 200,
  };

  if (params.isAttachProgramsQuery) {
    queryParams.service_period__in = params.servicePeriodIn;
  } else {
    if (params.companyUuid) {
      queryParams.company = params.companyUuid;
    }
    if (params.periodId) {
      queryParams.service_period = params.periodId;
    }
  }

  if (params.programId) {
    queryParams.uuid = params.programId;
  }

  if (params.valuesServices) {
    queryParams.service_types = params.valuesServices;
  }

  if (params.cityId) {
    queryParams.city = params.cityId;
  }

  const token = localStorage.getItem('token');

  const res = await fetch(`${API_URL}/programs/?${queryString.stringify(queryParams)}`, {
    method: 'GET',
    headers: {
      Accept: `${HEADER_ACCEPT_HR}`,
      Authorization: `Bearer ${token}`,
    },
  });

  if (res.ok) {
    return res.json();
  }
  throw new FetchError(res);
};
